/**
 * Think job cards — client types + pure helpers for Agent `state.jobs`
 * and `data-pcbx-job` history parts. Job tools live on the Think DO (M1);
 * this module only shapes UI. Never polls. Never enqueues or cancels.
 */

import { parseSiteInstantMs } from "./site-instant.ts";

export const THINK_JOB_STATES = ["queued", "running", "succeeded", "failed", "cancelled"] as const;
export type ThinkJobState = (typeof THINK_JOB_STATES)[number];

export const THINK_JOB_TOOL_NAMES = [
  "import_project",
  "run_checks",
  "circuit_diff",
  "agent_findings",
  "cancel_job",
] as const;
export type ThinkJobToolName = (typeof THINK_JOB_TOOL_NAMES)[number];

/** Tools that render a JobCard (cancel_job updates the original card). */
export const JOB_CARD_TOOL_NAMES = [
  "import_project",
  "run_checks",
  "circuit_diff",
  "agent_findings",
] as const;
export type JobCardToolName = (typeof JOB_CARD_TOOL_NAMES)[number];

export const PCBX_JOB_PART_TYPE = "data-pcbx-job" as const;

export const JOB_OK = "#3D9B6A";
export const JOB_COPPER = "#C45C26";
export const JOB_FALCON = "#E31C25";
export const JOB_CANCELLED = "#6E6E73";
export const JOB_INK_55 = "rgba(236, 236, 241, 0.55)";
/** Meta text on raised #141414 — ≥4.5:1. Do not paint cancelled copy with JOB_CANCELLED. */
export const JOB_META = "rgba(236, 236, 241, 0.72)";
export const JOB_RAISED = "#141414";
export const JOB_HAIRLINE = "rgba(255, 255, 255, 0.10)";

export type ThinkJobResult = {
  rev?: string | null;
  fromOid?: string | null;
  toOid?: string | null;
  gitOid?: string | null;
  paths?: number | null;
  nets?: number | null;
  findings?: number | null;
  count?: number | null;
  label?: string | null;
};

export type ThinkJob = {
  id: string;
  kind: ThinkJobToolName;
  state: ThinkJobState;
  title?: string | null;
  zipLabel?: string | null;
  profile?: string | null;
  rev?: string | null;
  reviewId?: string | null;
  reviewNumber?: number | null;
  createdAt?: string | null;
  queuedAt?: string | null;
  startedAt?: string | null;
  updatedAt?: string | null;
  finishedAt?: string | null;
  cancelledAt?: string | null;
  elapsedMs?: number | null;
  runnerNote?: string | null;
  error?: string | null;
  cancelledBy?: string | null;
  result?: ThinkJobResult | null;
};

export type PcbxJobWake = {
  id: string;
  state: ThinkJobState;
};

export type ThinkAgentJobsState = {
  jobs?: unknown;
};

export function isThinkJobState(value: unknown): value is ThinkJobState {
  return typeof value === "string" && (THINK_JOB_STATES as readonly string[]).includes(value);
}

export function isThinkJobToolName(value: unknown): value is ThinkJobToolName {
  return typeof value === "string" && (THINK_JOB_TOOL_NAMES as readonly string[]).includes(value);
}

/**
 * Agent `state.jobs` stores workbench SQL kinds (`import_kicad`, `scene`).
 * JobCard tools use Think names (`import_project`, `circuit_diff`, …).
 * Without this map, live succeeded jobs are dropped and the card stays queued.
 */
export function normalizeThinkJobKind(
  value: unknown,
  extra?: Record<string, unknown> | null,
): ThinkJobToolName | null {
  if (isThinkJobToolName(value)) return value;
  if (value === "import_kicad") return "import_project";
  if (value === "scene") {
    const detail =
      (typeof extra?.kind_detail === "string" && extra.kind_detail.trim()) ||
      (typeof extra?.kindDetail === "string" && extra.kindDetail.trim()) ||
      (typeof extra?.detail === "string" && extra.detail.trim()) ||
      "";
    if (detail === "circuit_diff" || detail === "agent_findings") return detail;
  }
  return null;
}

export function isInternalWakeText(text: string): boolean {
  const t = text ?? "";
  return (
    t.includes("Think resumed · job:") ||
    t.includes("A background job you started has reached a terminal state") ||
    /\bCall get_job\b/.test(t)
  );
}

export function isJobCardToolName(value: unknown): value is JobCardToolName {
  return typeof value === "string" && (JOB_CARD_TOOL_NAMES as readonly string[]).includes(value);
}

export function jobToolNameFromPart(
  type: string,
  toolName?: string,
): ThinkJobToolName | null {
  if (toolName && isThinkJobToolName(toolName)) return toolName;
  if (type.startsWith("tool-")) {
    const name = type.slice("tool-".length);
    if (isThinkJobToolName(name)) return name;
  }
  return null;
}

export function jobCardDomId(jobId: string): string {
  return `job-card-${jobId}`;
}

export function shortOid(oid: string | null | undefined, n = 7): string {
  if (!oid) return "";
  const trimmed = oid.trim();
  if (!trimmed) return "";
  return trimmed.length > n ? trimmed.slice(0, n) : trimmed;
}

/** Plate: `a3f91c2…370f1c2` (ellipsis, 7-char oids). */
export function formatOidRange(from?: string | null, to?: string | null): string | null {
  const a = shortOid(from, 7);
  const b = shortOid(to, 7);
  if (a && b) return `${a}…${b}`;
  if (a) return a;
  if (b) return b;
  return null;
}

/** Plate clock: `m:ss` (no fake percent). */
export function formatJobClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function jobElapsedMs(job: ThinkJob, now: number): number {
  const live = job.state === "queued" || job.state === "running";
  const start =
    parseSiteInstantMs(job.startedAt) ??
    parseSiteInstantMs(job.queuedAt) ??
    parseSiteInstantMs(job.createdAt);
  if (live) {
    if (start == null) {
      return typeof job.elapsedMs === "number" && Number.isFinite(job.elapsedMs)
        ? Math.max(0, job.elapsedMs)
        : 0;
    }
    return Math.max(0, now - start);
  }
  if (typeof job.elapsedMs === "number" && Number.isFinite(job.elapsedMs)) {
    return Math.max(0, job.elapsedMs);
  }
  const end = parseSiteInstantMs(job.finishedAt) ?? parseSiteInstantMs(job.cancelledAt);
  if (start != null && end != null) return Math.max(0, end - start);
  const created = parseSiteInstantMs(job.createdAt);
  const updated = parseSiteInstantMs(job.updatedAt);
  if (created != null && updated != null) return Math.max(0, updated - created);
  return 0;
}

export function thinkJobTitle(job: ThinkJob): string {
  const explicit = job.title?.trim();
  if (explicit) return explicit;
  switch (job.kind) {
    case "import_project":
      return job.zipLabel?.trim() ? `Import ${job.zipLabel.trim()}` : "Import project";
    case "run_checks": {
      const rev = job.rev?.trim() || job.result?.rev?.trim();
      const check = checkProfileLabel(job.profile);
      if (rev) return `${check} on ${rev}`;
      return `${check} checks`;
    }
    case "circuit_diff":
      return job.reviewNumber != null ? `Circuit diff · change #${job.reviewNumber}` : "Circuit diff";
    case "agent_findings":
      return job.reviewNumber != null ? `Findings · change #${job.reviewNumber}` : "Findings";
    case "cancel_job":
      return "Cancel job";
  }
}

export function thinkJobRunnerNote(job: ThinkJob): string {
  const note = job.runnerNote?.trim();
  if (note) return note;
  switch (job.kind) {
    case "import_project":
      return "Importing zip…";
    case "run_checks":
      return "Running checks…";
    case "circuit_diff":
      return "Diffing nets…";
    case "agent_findings":
      return "Summarizing findings…";
    case "cancel_job":
      return "Cancelling…";
  }
}

function countChip(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

export function thinkJobResultChips(job: ThinkJob): string[] {
  const result = job.result;
  if (!result) return [];
  const chips: string[] = [];
  const rev = result.rev?.trim() || job.rev?.trim();
  if (rev) chips.push(`rev ${rev}`);
  const range = formatOidRange(result.fromOid, result.toOid);
  if (range) chips.push(range);
  else if (result.gitOid) {
    const oid = shortOid(result.gitOid, 7);
    if (oid) chips.push(oid);
  }
  if (typeof result.paths === "number") chips.push(countChip(result.paths, "path", "paths"));
  if (typeof result.nets === "number") chips.push(countChip(result.nets, "net", "nets"));
  if (typeof result.findings === "number") chips.push(countChip(result.findings, "finding", "findings"));
  if (typeof result.count === "number" && result.paths == null && result.nets == null && result.findings == null) {
    chips.push(String(result.count));
  }
  if (result.label?.trim()) chips.push(result.label.trim());
  return chips;
}

export function jobBarColor(state: ThinkJobState): string {
  switch (state) {
    case "queued":
      return JOB_INK_55;
    case "running":
      return JOB_COPPER;
    case "succeeded":
      return JOB_OK;
    case "failed":
      return JOB_FALCON;
    case "cancelled":
      return JOB_CANCELLED;
  }
}

/** Human-readable job type. Keep generic — M1 may drop live `run_checks`. */
export function thinkJobKindLabel(kind: string): string {
  switch (kind) {
    case "import_project":
      return "Import";
    case "run_checks":
      return "Checks";
    case "circuit_diff":
      return "Circuit diff";
    case "agent_findings":
      return "Findings";
    case "cancel_job":
      return "Cancel";
    default:
      return kind.replace(/_/g, " ").trim() || "Job";
  }
}

function checkProfileLabel(profile?: string | null): string {
  const raw = profile?.trim();
  if (!raw) return "ERC";
  return raw.toUpperCase();
}

/** Plate clock: `13:59` (minute precision). */
export function formatJobEndTime(iso?: string | null): string | null {
  const ms = parseSiteInstantMs(iso);
  if (ms == null) return null;
  const d = new Date(ms);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

export function formatJobMetaLine(job: ThinkJob, clock: string): string {
  if (job.state === "queued") return `${job.id} waiting ${clock}`;
  if (job.state === "running") return `${job.id} ${clock}`;
  if (job.state === "cancelled") {
    const who = job.cancelledBy?.trim();
    return who ? `${job.id} at ${clock} · by ${who}` : `${job.id} at ${clock}`;
  }
  if (job.state === "failed") return `${job.id} after ${clock}`;
  const end = formatJobEndTime(job.finishedAt);
  return end ? `${job.id} ${clock} · ${end}` : `${job.id} ${clock}`;
}

export function wakeJobId(id: string): string {
  return id.replace(/^job[_-]/i, "");
}

export function jobStateLabel(state: ThinkJobState): string {
  switch (state) {
    case "queued":
      return "Queued";
    case "running":
      return "Running";
    case "succeeded":
      return "Succeeded";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
  }
}

export function jobPillLabel(count: number): string {
  return count === 1 ? "1 job" : `${count} jobs`;
}

const PILL_RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["minute", 60 * 1000],
];

/** Terminal pill hint — relative finish time, not a live clock. */
export function formatJobPillTerminalHint(job: ThinkJob, nowMs: number): string {
  const endMs =
    parseSiteInstantMs(job.finishedAt) ??
    parseSiteInstantMs(job.cancelledAt) ??
    parseSiteInstantMs(job.updatedAt);
  if (endMs != null) {
    const delta = endMs - nowMs;
    const abs = Math.abs(delta);
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    for (const [unit, size] of PILL_RELATIVE_UNITS) {
      if (abs >= size || unit === "minute") {
        const value = Math.round(delta / size);
        return rtf.format(value, unit);
      }
    }
  }
  return jobStateLabel(job.state);
}

export function isActiveJobState(state: ThinkJobState): boolean {
  return state === "queued" || state === "running";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function pickStr(rec: Record<string, unknown> | null, ...keys: string[]): string | null {
  if (!rec) return null;
  for (const key of keys) {
    const value = str(rec[key]);
    if (value) return value;
  }
  return null;
}

function pickNum(rec: Record<string, unknown> | null, ...keys: string[]): number | null {
  if (!rec) return null;
  for (const key of keys) {
    const value = num(rec[key]);
    if (value !== null) return value;
  }
  return null;
}

export function pickJobId(value: unknown): string | null {
  const rec = asRecord(value);
  return pickStr(rec, "id", "job_id", "jobId");
}

function parseResult(value: unknown): ThinkJobResult | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const result: ThinkJobResult = {};
  const rev = pickStr(rec, "rev", "revision");
  const fromOid = pickStr(rec, "fromOid", "from_oid", "from");
  const toOid = pickStr(rec, "toOid", "to_oid", "to");
  const gitOid = pickStr(rec, "gitOid", "git_oid", "oid", "head_oid");
  const paths = pickNum(rec, "paths", "path_count");
  const nets = pickNum(rec, "nets", "net_count");
  const findings = pickNum(rec, "findings", "finding_count");
  const count = pickNum(rec, "count");
  const label = pickStr(rec, "label");
  if (rev) result.rev = rev;
  if (fromOid) result.fromOid = fromOid;
  if (toOid) result.toOid = toOid;
  if (gitOid) result.gitOid = gitOid;
  if (paths != null) result.paths = paths;
  if (nets != null) result.nets = nets;
  if (findings != null) result.findings = findings;
  if (count != null) result.count = count;
  if (label) result.label = label;
  return Object.keys(result).length > 0 ? result : null;
}

type ThinkJobDraft = Omit<ThinkJob, "state"> & { state?: ThinkJobState };

function parseThinkJobDraft(value: unknown, fallbackKind?: ThinkJobToolName): ThinkJobDraft | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const id = pickJobId(rec);
  const kindRaw = pickStr(rec, "kind", "tool", "toolName", "tool_name");
  const kind = normalizeThinkJobKind(kindRaw, rec) ?? fallbackKind;
  if (!id || !kind) return null;
  const stateRaw = pickStr(rec, "state", "status");
  if (stateRaw && !isThinkJobState(stateRaw)) {
    console.warn("[think-jobs] dropped job with unknown state", { id, state: stateRaw });
    return null;
  }
  const nested = asRecord(rec.result) ? parseResult(rec.result) : parseResult(rec);
  const state = isThinkJobState(stateRaw) ? stateRaw : undefined;
  const failed = state === "failed";
  const draft: ThinkJobDraft = {
    id,
    kind,
    title: pickStr(rec, "title"),
    zipLabel: pickStr(rec, "zipLabel", "zip_label", "zip", "label"),
    profile: pickStr(rec, "profile"),
    rev: pickStr(rec, "rev", "revision"),
    reviewId: pickStr(rec, "reviewId", "review_id"),
    reviewNumber: pickNum(rec, "reviewNumber", "review_number"),
    createdAt: pickStr(rec, "createdAt", "created_at"),
    queuedAt: pickStr(rec, "queuedAt", "queued_at"),
    startedAt: pickStr(rec, "startedAt", "started_at"),
    updatedAt: pickStr(rec, "updatedAt", "updated_at"),
    finishedAt: pickStr(rec, "finishedAt", "finished_at"),
    cancelledAt: pickStr(rec, "cancelledAt", "cancelled_at"),
    elapsedMs: pickNum(rec, "elapsedMs", "elapsed_ms"),
    runnerNote: pickStr(rec, "runnerNote", "runner_note", "note"),
    error: pickStr(rec, "error", "errorText", "error_text") ?? (failed ? pickStr(rec, "message") : null),
    cancelledBy: pickStr(rec, "cancelledBy", "cancelled_by"),
    result: nested,
  };
  if (state) draft.state = state;
  return draft;
}

export function parseThinkJob(value: unknown, fallbackKind?: ThinkJobToolName): ThinkJob | null {
  return finalizeThinkJob(parseThinkJobDraft(value, fallbackKind));
}

function finalizeThinkJob(job: ThinkJobDraft | null | undefined): ThinkJob | null {
  if (!job?.id || !job.kind || !job.state) return null;
  return { ...job, state: job.state };
}

function defined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function mergeThinkJob(...layers: Array<ThinkJobDraft | ThinkJob | null | undefined>): ThinkJob | null {
  const present = layers.filter(defined);
  if (present.length === 0) return null;
  const first = present[0]!;
  const merged: ThinkJobDraft = { ...first, result: first.result ? { ...first.result } : undefined };
  for (const layer of present.slice(1)) {
    merged.id = layer.id || merged.id;
    merged.kind = layer.kind || merged.kind;
    if (layer.state) merged.state = layer.state;
    if (layer.title) merged.title = layer.title;
    if (layer.zipLabel) merged.zipLabel = layer.zipLabel;
    if (layer.profile) merged.profile = layer.profile;
    if (layer.rev) merged.rev = layer.rev;
    if (layer.reviewId) merged.reviewId = layer.reviewId;
    if (layer.reviewNumber != null) merged.reviewNumber = layer.reviewNumber;
    if (layer.createdAt) merged.createdAt = layer.createdAt;
    if (layer.queuedAt) merged.queuedAt = layer.queuedAt;
    if (layer.startedAt) merged.startedAt = layer.startedAt;
    if (layer.updatedAt) merged.updatedAt = layer.updatedAt;
    if (layer.finishedAt) merged.finishedAt = layer.finishedAt;
    if (layer.cancelledAt) merged.cancelledAt = layer.cancelledAt;
    if (layer.elapsedMs != null) merged.elapsedMs = layer.elapsedMs;
    if (layer.runnerNote) merged.runnerNote = layer.runnerNote;
    if (layer.error) merged.error = layer.error;
    if (layer.cancelledBy) merged.cancelledBy = layer.cancelledBy;
    if (layer.result) {
      const patch = Object.fromEntries(
        Object.entries(layer.result).filter(([, value]) => value != null),
      ) as ThinkJobResult;
      merged.result = { ...merged.result, ...patch };
    }
  }
  return finalizeThinkJob(merged);
}

export function thinkJobsFromWorkbenchItems(items: unknown): Record<string, ThinkJob> {
  const out: Record<string, ThinkJob> = {};
  if (!Array.isArray(items)) return out;
  for (const row of items) {
    const parsed = parseThinkJob(row);
    if (parsed) out[parsed.id] = parsed;
  }
  return out;
}

/** Live WS jobs plus D1-only historical jobs (pill / JobCard when chat window missed them). */
export function unionThinkJobs(
  live: Record<string, ThinkJob>,
  extra: Record<string, ThinkJob>,
): Record<string, ThinkJob> {
  const out: Record<string, ThinkJob> = { ...live };
  for (const [id, row] of Object.entries(extra)) {
    const current = out[id];
    if (current) {
      const next = mergeThinkJob(current, {
        id: current.id,
        kind: current.kind,
        state: current.state,
        createdAt: current.createdAt || row.createdAt,
        startedAt: current.startedAt || row.startedAt,
        updatedAt: current.updatedAt || row.updatedAt,
        finishedAt: current.finishedAt || row.finishedAt,
        elapsedMs: current.elapsedMs ?? row.elapsedMs,
      });
      if (next) out[id] = next;
    } else {
      out[id] = row;
    }
  }
  return out;
}

export type AgentHydrateSnapshot = {
  ok: boolean;
  skipped?: boolean;
  error?: string;
  snapshot_id?: string;
  file_count?: number;
};

export type AgentChatTurnErrorSnapshot = {
  kind: string;
  class?: string;
  message: string;
  requestId?: string | null;
};

export function chatTurnErrorFromAgentState(state: unknown): AgentChatTurnErrorSnapshot | null {
  const rec = asRecord(state);
  const raw = rec?.chatTurnError;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const message = typeof row.message === "string" ? row.message.trim() : "";
  if (!message) return null;
  return {
    kind: typeof row.kind === "string" ? row.kind : "unknown",
    class: typeof row.class === "string" ? row.class : undefined,
    message,
    requestId: typeof row.requestId === "string" ? row.requestId : null,
  };
}

export function hydrateFromAgentState(state: unknown): AgentHydrateSnapshot | null {
  const rec = asRecord(state);
  const raw = rec?.lastHydrate;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.ok !== "boolean") return null;
  return {
    ok: row.ok,
    skipped: typeof row.skipped === "boolean" ? row.skipped : undefined,
    error: typeof row.error === "string" ? row.error : undefined,
    snapshot_id: typeof row.snapshot_id === "string" ? row.snapshot_id : undefined,
    file_count: typeof row.file_count === "number" ? row.file_count : undefined,
  };
}

export function jobsFromAgentState(state: unknown): Record<string, ThinkJob> {
  const rec = asRecord(state);
  if (!rec || !("jobs" in rec)) return {};
  const raw = rec.jobs;
  const out: Record<string, ThinkJob> = {};
  const ingest = (value: unknown, key?: string) => {
    const parsed =
      parseThinkJobDraft(value) ?? (key ? parseThinkJobDraft({ ...(asRecord(value) ?? {}), id: key }) : null);
    if (parsed) {
      const next = mergeThinkJob(out[parsed.id], parsed);
      if (next) out[parsed.id] = next;
    }
  };
  if (Array.isArray(raw)) {
    for (const row of raw) ingest(row);
    return out;
  }
  const bag = asRecord(raw);
  if (!bag) return out;
  if (Array.isArray(bag.items)) {
    for (const row of bag.items) ingest(row);
    return out;
  }
  for (const [key, value] of Object.entries(bag)) ingest(value, key);
  return out;
}

export function resolveJobForCard(args: {
  toolName: ThinkJobToolName;
  input?: unknown;
  output?: unknown;
  liveJobs: Record<string, ThinkJob>;
}): ThinkJob | null {
  const fromOutput = parseThinkJobDraft(args.output, args.toolName);
  const fromInput = parseThinkJobDraft(args.input, args.toolName);
  const id = fromOutput?.id || fromInput?.id || pickJobId(args.output) || pickJobId(args.input);
  if (!id) return null;
  const live = args.liveJobs[id];
  let state = live?.state ?? fromOutput?.state ?? fromInput?.state;
  if (!state && (pickJobId(args.output) || pickJobId(args.input))) {
    state = "queued";
  }
  if (!state) return null;
  const seed: ThinkJob = {
    id,
    kind: args.toolName,
    state,
  };
  return mergeThinkJob(seed, fromInput, fromOutput, live);
}

export function parsePcbxJobPart(part: unknown): PcbxJobWake | null {
  const rec = asRecord(part);
  if (!rec || rec.type !== PCBX_JOB_PART_TYPE) return null;
  const data = asRecord(rec.data) ?? rec;
  const id = pickStr(data, "id", "job_id", "jobId");
  if (!id) return null;
  const stateRaw = pickStr(data, "state", "status");
  return { id, state: isThinkJobState(stateRaw) ? stateRaw : "succeeded" };
}

export function formatWakeMarker(wake: PcbxJobWake): string {
  return `Think resumed · job:${wakeJobId(wake.id)}:${wake.state}`;
}

export function collectJobsFromParts(
  parts: readonly unknown[],
  live: Record<string, ThinkJob>,
): Record<string, ThinkJob> {
  const out = { ...live };
  for (const part of parts) {
    const rec = asRecord(part);
    if (!rec || typeof rec.type !== "string") continue;
    const toolName = jobToolNameFromPart(rec.type, str(rec.toolName) ?? str(rec.tool_name) ?? undefined);
    if (!toolName || toolName === "cancel_job") continue;
    const job = resolveJobForCard({
      toolName,
      input: rec.input,
      output: rec.output,
      liveJobs: live,
    });
    if (job) out[job.id] = mergeThinkJob(out[job.id], job) ?? job;
  }
  return out;
}

export function sortJobsForPill(jobs: readonly ThinkJob[]): ThinkJob[] {
  return [...jobs].sort((a, b) => {
    const aActive = isActiveJobState(a.state) ? 0 : 1;
    const bActive = isActiveJobState(b.state) ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    return (parseSiteInstantMs(b.createdAt) ?? 0) - (parseSiteInstantMs(a.createdAt) ?? 0);
  });
}

export function retryViaThinkText(job: ThinkJob): string {
  return `Retry ${job.kind} job ${job.id} via Think.`;
}

export function cancelViaThinkText(job: ThinkJob): string {
  return `Cancel job ${job.id} with cancel_job.`;
}

/** Fixed Retry copy for needs_human (toolCallId only — no forged tool name). */
export function resumeWriteViaThinkText(toolCallId: string): string {
  return `Resume the pending write tool call (${toolCallId}).`;
}

export function needsHumanResumePrefix(toolCallId: string): string {
  return `Resume the pending write tool call (${toolCallId})`;
}

export function needsHumanConsumedStorageKey(projectId: string): string {
  return `pcbx-needs-human-consumed:${projectId}`;
}

export function loadNeedsHumanConsumed(projectId: string): Set<string> {
  if (typeof sessionStorage === "undefined") return new Set();
  try {
    const raw = sessionStorage.getItem(needsHumanConsumedStorageKey(projectId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string" && id.length > 0));
  } catch {
    return new Set();
  }
}

export function persistNeedsHumanConsumed(projectId: string, consumed: ReadonlySet<string>): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      needsHumanConsumedStorageKey(projectId),
      JSON.stringify([...consumed]),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

/** Derive consumed retries from transcript (survives refresh when sessionStorage cleared). */
export function needsHumanConsumedFromMessages(
  messages: ReadonlyArray<{ role?: string; parts?: unknown }> | undefined,
): Set<string> {
  const out = new Set<string>();
  if (!messages?.length) return out;
  for (const message of messages) {
    if (message.role !== "user") continue;
    const parts = message.parts;
    if (!Array.isArray(parts)) continue;
    for (const part of parts) {
      if (!part || typeof part !== "object") continue;
      const text = (part as { type?: unknown; text?: unknown }).text;
      if (typeof text !== "string") continue;
      const match = text.match(/^Resume the pending write tool call \(([A-Za-z0-9_-]{1,64})\)/);
      if (match?.[1]) out.add(match[1]);
    }
  }
  return out;
}

export function canShowNeedsHumanRetry(
  role: string | null | undefined,
  toolCallId: string,
  consumed: ReadonlySet<string>,
): boolean {
  if (!toolCallId || consumed.has(toolCallId)) return false;
  if (!role || role === "viewer" || role === "guest" || role === "review") return false;
  return true;
}
